/**
 * /app/pedidos — listado Ventas (POS + online). SCRUM-229/232.
 * Detalle: /app/pedidos/$nro
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { CrudEmptyState, CrudShellHeader, DataTable, FilterBar, Loading } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { PedidosTour } from "@/components/ProductTour";
import { ClinicalPageShell } from "@/components/clinical";
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge";
import {
  formatOrderDate,
  formatOrderPesos,
  type OrderDetail,
} from "@/components/orders/OrderDetailPanel";
import { ClipboardList, RefreshCw, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ORDER_STATUS_LABELS, ORDER_STATUSES } from "@/domain/sm-models";
import type { OrderStatus } from "@/domain/sm-models";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { formatOrderLabel, formatOrderNumber } from "@/lib/order-number";
import {
  formatOrderInvoiceLabel,
  formatOrderPaymentLabel,
  inferOrderChannel,
} from "@/lib/order-channel";

export const Route = createFileRoute("/app/pedidos")({
  component: PedidosPage,
  head: () => ({ meta: [{ title: "Ventas — Structura Market" }] }),
});

type Order = OrderDetail;

type StatusCardFilter = "activos" | "todos" | "entregados" | "cancelados";

const ORDERS_PAGE_SIZE = 24;

const STATUS_CARDS: { id: StatusCardFilter; label: string; description: string }[] = [
  { id: "activos", label: "Activos", description: "En curso" },
  { id: "todos", label: "Todos", description: "Histórico completo" },
  { id: "entregados", label: "Entregados", description: "Cerrados OK" },
  { id: "cancelados", label: "Cancelados", description: "Anulados" },
];

const ACTIVE_STATUSES: OrderStatus[] = [
  "PENDIENTE", "CONFIRMADO", "AJUSTADO_PESADO", "PENDIENTE_PAGO",
  "PAGADO", "EN_PREPARACION", "LISTO", "LISTO_RETIRO", "EN_CAMINO",
];

/** SCRUM-400 — último filtro de ventas usado, por tenant (localStorage = por navegador/usuario). */
const STATUS_FILTER_STORAGE_PREFIX = "sm-pedidos-status-filter:";
const isStatusCardFilter = (v: unknown): v is StatusCardFilter =>
  v === "activos" || v === "todos" || v === "entregados" || v === "cancelados";

function PedidosPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusCard, setStatusCard] = useState<StatusCardFilter>("activos");
  const [statusCombo, setStatusCombo] = useState<OrderStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // SCRUM-400: restaura el último filtro usado por este usuario para este negocio.
  useEffect(() => {
    if (!tid) return;
    try {
      const saved = localStorage.getItem(`${STATUS_FILTER_STORAGE_PREFIX}${tid}`);
      if (isStatusCardFilter(saved)) setStatusCard(saved);
    } catch {
      /* localStorage no disponible — se queda con el default "activos" */
    }
  }, [tid]);

  useEffect(() => {
    if (!tid) return;
    try {
      localStorage.setItem(`${STATUS_FILTER_STORAGE_PREFIX}${tid}`, statusCard);
    } catch {
      /* ignorar — preferencia de UI, no crítica */
    }
  }, [tid, statusCard]);

  const load = useCallback(() => {
    if (!tid) return;
    setLoading(true);
    apiGetJson<Order[]>("/api/orders", tid)
      .then(setOrders)
      .catch(() => toast.error("Error al cargar ventas"))
      .finally(() => setLoading(false));
  }, [tid]);

  useEffect(load, [load]);

  useEffect(() => {
    setPage(1);
  }, [statusCard, statusCombo, search]);

  function openOrder(order: Order) {
    const nro =
      formatOrderNumber(order.orderNumber) ??
      (order.orderNumber != null ? String(order.orderNumber) : null) ??
      order.id;
    void navigate({ to: "/app/pedidos/$nro", params: { nro } });
  }

  const filtered = orders.filter((o) => {
    if (statusCard === "activos" && !ACTIVE_STATUSES.includes(o.status)) return false;
    if (statusCard === "entregados" && o.status !== "ENTREGADO") return false;
    if (statusCard === "cancelados" && o.status !== "CANCELADO") return false;
    if (statusCombo !== "all" && o.status !== statusCombo) return false;
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const nro = formatOrderNumber(o.orderNumber) ?? "";
    const label = formatOrderLabel(o.orderNumber).toLowerCase();
    return (
      o.customerName.toLowerCase().includes(q) ||
      o.customerEmail.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q) ||
      nro.includes(q) ||
      String(o.orderNumber ?? "").includes(q) ||
      label.includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ORDERS_PAGE_SIZE));
  const pageOrders = filtered.slice((page - 1) * ORDERS_PAGE_SIZE, page * ORDERS_PAGE_SIZE);

  const counts = {
    activos: orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length,
    todos: orders.length,
    entregados: orders.filter((o) => o.status === "ENTREGADO").length,
    cancelados: orders.filter((o) => o.status === "CANCELADO").length,
  };

  const pedidoColumns: DataTableColumn<Order>[] = [
    {
      key: "id",
      header: "ID",
      className: "whitespace-nowrap font-semibold tabular-nums",
      render: (order) => (
        <span className="inline-flex items-center gap-1" title={formatOrderLabel(order.orderNumber)}>
          {order.orderNumber ?? "—"}
          {order.tienePesables ? <Scale className="h-3 w-3 text-orange-500" /> : null}
        </span>
      ),
    },
    {
      key: "fecha",
      header: "Fecha",
      className: "whitespace-nowrap text-muted-foreground",
      render: (order) => (
        <span title={order.createdAt}>{formatOrderDate(order.createdAt)}</span>
      ),
    },
    {
      key: "canal",
      header: "Canal",
      className: "whitespace-nowrap",
      render: (order) => inferOrderChannel(order),
    },
    {
      key: "cliente",
      header: "Cliente",
      className: "max-w-[10rem] truncate",
      render: (order) => (
        <span className="block truncate" title={order.customerEmail || order.customerName}>
          {order.customerName || "—"}
        </span>
      ),
    },
    {
      key: "estado",
      header: "Estado",
      className: "whitespace-nowrap",
      render: (order) => <OrderStatusBadge status={order.status} />,
    },
    {
      key: "factura",
      header: "Factura",
      className: "whitespace-nowrap text-muted-foreground",
      render: (order) => formatOrderInvoiceLabel(order),
    },
    {
      key: "pago",
      header: "Pago",
      className: "max-w-[7rem] truncate text-muted-foreground",
      render: (order) => (
        <span className="block truncate" title={order.paymentMethod ?? undefined}>
          {formatOrderPaymentLabel(order.paymentMethod)}
        </span>
      ),
    },
    {
      key: "monto",
      header: "Monto",
      className: "whitespace-nowrap text-right font-semibold tabular-nums",
      render: (order) => formatOrderPesos(order.precioFinal ?? order.precioEstimado),
    },
  ];

  return (
    <div className="flex flex-col gap-0 p-0">
      <PedidosTour />
      <MerchantExperienceBar screenId="pedidos" />
      <CrudShellHeader
        title="Ventas"
        description="POS y venta online · tocá una fila para ver el detalle"
        actions={
          <Button variant="outline" size="sm" className="gap-1.5" onClick={load}>
            <RefreshCw className="h-4 w-4" /> Actualizar
          </Button>
        }
      />

      <ClinicalPageShell offset="none" className="space-y-4 pt-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-tour="pedidos-tabs">
          {STATUS_CARDS.map((card) => {
            const active = statusCard === card.id && statusCombo === "all";
            const count = counts[card.id];
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => {
                  setStatusCard(card.id);
                  setStatusCombo("all");
                }}
                className={cn(
                  "rounded-lg border px-3 py-2 text-left transition-all hover:border-primary/40",
                  active ? "border-primary bg-primary/10 shadow-sm" : "border-border bg-card",
                )}
              >
                <p className="text-sm font-semibold text-foreground">{card.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{card.description}</p>
                <p className="mt-1 text-base font-bold tabular-nums text-foreground">{count}</p>
              </button>
            );
          })}
        </div>

        <FilterBar
          search={{
            value: search,
            onChange: setSearch,
            placeholder: "Buscar N°, cliente, email…",
            dataTour: "pedidos-search",
          }}
        >
          <Select
            value={statusCombo}
            onValueChange={(v) => setStatusCombo(v as OrderStatus | "all")}
          >
            <SelectTrigger className="w-full sm:w-[220px]" data-tour="pedidos-status-filter">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {ORDER_STATUSES.map((st) => (
                <SelectItem key={st} value={st}>
                  {ORDER_STATUS_LABELS[st]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterBar>

        {loading ? (
          <Loading variant="list" />
        ) : (
          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            <DataTable
              density="compact"
              columns={pedidoColumns}
              rows={pageOrders}
              getRowKey={(o) => o.id}
              onRowClick={(o) => openOrder(o)}
              emptyState={
                <CrudEmptyState
                  icon={<ClipboardList className="h-8 w-8" />}
                  title={
                    search || statusCombo !== "all"
                      ? "Sin resultados para los filtros aplicados"
                      : statusCard === "activos"
                        ? "No hay ventas activas"
                        : "Sin ventas"
                  }
                />
              }
            />
            {filtered.length > 0 ? (
              <div className="flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  Página {page} de {totalPages} · {filtered.length} venta
                  {filtered.length !== 1 ? "s" : ""}
                </p>
                {totalPages > 1 ? (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setPage((p) => Math.max(1, p - 1));
                          }}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        const p =
                          totalPages <= 5
                            ? i + 1
                            : Math.min(Math.max(page - 2, 1), totalPages - 4) + i;
                        return (
                          <PaginationItem key={p}>
                            <PaginationLink
                              href="#"
                              isActive={p === page}
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(p);
                              }}
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            setPage((p) => Math.min(totalPages, p + 1));
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </ClinicalPageShell>
    </div>
  );
}
